import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Grid,
  CircularProgress,
  Grid2,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useNavigate, useParams } from 'react-router-dom'
import userService, { User, ChangePasswordInput } from '../services/userService'
import InputField from '../components/common/FormFields/InputField'

const UserChangePassword: React.FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { username } = useParams<{ username: string }>()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [user, setUser] = useState<User | null>(null)
  const [currentPassword, setCurrentPassword] = useState<string>('')
  const [newPassword, setNewPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [initialLoading, setInitialLoading] = useState<boolean>(true)

  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  // Password validation function
  const isPasswordValid = (password: string): boolean => {
    return password.length >= 4
  }

  // More comprehensive password validation
  const validatePassword = (
    password: string,
  ): { valid: boolean; message: string } => {
    if (password.length < 6) {
      return {
        valid: false,
        message: 'รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร',
      }
    }

    // Add more complex validation if needed
    // For example, check for uppercase, lowercase, numbers, special characters
    // const hasUppercase = /[A-Z]/.test(password);
    // const hasLowercase = /[a-z]/.test(password);
    // const hasNumbers = /\d/.test(password);
    // const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    // if (!hasUppercase || !hasLowercase || !hasNumbers || !hasSpecialChars) {
    //   return {
    //     valid: false,
    //     message: 'รหัสผ่านต้องประกอบด้วยตัวอักษรพิมพ์ใหญ่, พิมพ์เล็ก, ตัวเลข และอักขระพิเศษ'
    //   };
    // }

    return { valid: true, message: '' }
  }

  // Check if password is valid
  const passwordValidation =
    newPassword !== ''
      ? validatePassword(newPassword)
      : { valid: true, message: '' }
  const passwordError = newPassword !== '' && !passwordValidation.valid

  useEffect(() => {
    const fetchUser = async () => {
      if (!username) return

      try {
        const userData = await userService.getUser(username)
        setUser(userData)
      } catch (error) {
        console.error('Error fetching user:', error)
        setSnackbar({
          open: true,
          message: 'ไม่สามารถโหลดข้อมูลผู้ใช้งานได้',
          severity: 'error',
        })
      } finally {
        setInitialLoading(false)
      }
    }

    fetchUser()
  }, [username])

  const handleBack = () => {
    navigate('/users')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!username) return

    // ตรวจสอบข้อมูล
    if (!newPassword) {
      setSnackbar({
        open: true,
        message: 'กรุณากรอกรหัสผ่านปัจจุบันและรหัสผ่านใหม่',
        severity: 'error',
      })
      return
    }

    // ตรวจสอบความยาวรหัสผ่าน
    const validation = validatePassword(newPassword)
    if (!validation.valid) {
      setSnackbar({
        open: true,
        message: validation.message,
        severity: 'error',
      })
      return
    }

    if (newPassword !== confirmPassword) {
      setSnackbar({
        open: true,
        message: 'รหัสผ่านใหม่ไม่ตรงกัน กรุณาตรวจสอบอีกครั้ง',
        severity: 'error',
      })
      return
    }

    setLoading(true)

    try {
      const passwordData: ChangePasswordInput = {
        currentPassword,
        newPassword,
      }

      await userService.changePassword(username, passwordData)

      setSnackbar({
        open: true,
        message: 'เปลี่ยนรหัสผ่านสำเร็จ',
        severity: 'success',
      })

      // รอให้ Snackbar แสดงสักครู่ก่อนกลับไปหน้ารายการ
      setTimeout(() => {
        navigate('/users')
      }, 1500)
    } catch (error) {
      console.error('Error changing password:', error)
      setSnackbar({
        open: true,
        message: 'เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน',
        severity: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/users')
  }

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }))
  }

  if (initialLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (!user && !initialLoading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: '100%' }}>
        <Typography variant="h5" color="error">
          ไม่พบข้อมูลผู้ใช้งาน
        </Typography>
        <Button variant="contained" onClick={handleBack} sx={{ mt: 2 }}>
          กลับไปหน้ารายการ
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1">
          เปลี่ยนรหัสผ่าน
        </Typography>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ maxWidth: 800, mx: 'auto' }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              เปลี่ยนรหัสผ่านสำหรับผู้ใช้งาน: {user?.username}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              กรุณากรอกรหัสผ่านปัจจุบันและรหัสผ่านใหม่ที่ต้องการเปลี่ยน
            </Typography>
          </Box>

          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <InputField
                label="ชื่อผู้ใช้งาน"
                fullWidth
                value={user?.name || ''}
                disabled
                sx={{ mb: 2 }}
                name="username"
                onChange={() => {}}
              />
            </Grid2>
            {/* <Grid2 size={12}>
              <InputField
                label="รหัสผ่านปัจจุบัน"
                name="currentPassword"
                type="password"
                fullWidth
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                sx={{ mb: 2 }}
              />
            </Grid2> */}
            <Grid2 size={12}>
              <InputField
                label="รหัสผ่านใหม่"
                name="newPassword"
                type="password"
                fullWidth
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{ mb: 2 }}
                error={passwordError}
                helperText={
                  passwordError
                    ? passwordValidation.message
                    : 'รหัสผ่านควรมีความยาวอย่างน้อย 6 ตัวอักษร'
                }
              />
            </Grid2>
            <Grid2 size={12}>
              <InputField
                label="ยืนยันรหัสผ่านใหม่"
                name="confirmPassword"
                type="password"
                fullWidth
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                sx={{ mb: 2 }}
                error={
                  newPassword !== confirmPassword && confirmPassword !== ''
                }
                helperText={
                  newPassword !== confirmPassword && confirmPassword !== ''
                    ? 'รหัสผ่านไม่ตรงกัน'
                    : ''
                }
              />
            </Grid2>
          </Grid2>

          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
            >
              ยกเลิก
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={
                loading ||
                newPassword !== confirmPassword ||
                !newPassword ||
                passwordError
              }
              startIcon={
                loading ? <CircularProgress size={20} color="inherit" /> : null
              }
            >
              บันทึก
            </Button>
          </Box>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default UserChangePassword
